import { Entity } from "@common/types/entity";
import { formatISO } from "date-fns";
import { ReportFormat } from "@common/helpers/reports";
import {
    AccountTransactionsQuery,
    exportAccountTransactions,
} from "@api/accountingReports.api";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { pickXlsxExportRowVisibility } from "@components/accounting/xlsxExportRowVisibility";

interface GeneralLedgerDownloadParams {
    format: ReportFormat;
    startDate?: Date;
    endDate?: Date;
}

interface UseGeneralLedgerDownloadParams {
    requestDto: AccountTransactionsQuery;
    entityIds: number[];
    entitiesById: Map<number, Entity>;
    rowVisibility?: ReportSettingRowVisibility;
}

// Derive the filename from the requested scope (`entityIds`), not the resolved
// metadata: if the entity map is still loading or is partial, the export still
// covers every requested entity, so the name must reflect that count rather
// than silently undersizing it.
export function buildReportTitleLabel(
    entityIds: number[],
    entitiesById: Map<number, Entity>,
): string {
    const reportTypeTitle =
        ACCOUNTING_REPORT_LABELS[AccountingReportType.GENERAL_LEDGER];
    if (entityIds.length !== 1) {
        return `${reportTypeTitle} - Multi-entity (${entityIds.length})`;
    }
    const singleEntityName = entitiesById.get(entityIds[0])?.name;
    return singleEntityName
        ? `${singleEntityName} - ${reportTypeTitle}`
        : reportTypeTitle;
}

export function useGeneralLedgerDownload({
    requestDto,
    entityIds,
    entitiesById,
    rowVisibility,
}: UseGeneralLedgerDownloadParams) {
    return useFileDownload(
        async ({ format, startDate, endDate }: GeneralLedgerDownloadParams) => {
            if (!requestDto || entityIds.length === 0) {
                return;
            }

            const exportRowVisibility = pickXlsxExportRowVisibility(
                format,
                rowVisibility,
            );

            const response = await exportAccountTransactions(
                {
                    ...requestDto,
                    format,
                },
                exportRowVisibility,
            );

            if (!response) {
                return;
            }

            const startLabel = startDate
                ? formatISO(startDate, { representation: "date" })
                : "";
            const endLabel = endDate
                ? formatISO(endDate, { representation: "date" })
                : "";
            const titleLabel = buildReportTitleLabel(entityIds, entitiesById);
            const fallbackName = `${titleLabel} - ${startLabel} - ${endLabel}.${format}`;

            saveBlobResponse(response, fallbackName);
        },
    );
}
